import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { store } from 'redux-rest-reducer';
import styled from 'styled-components';

import { Separator, FlexView } from '@cuemath/comps';
import { Header, Grid } from 'semantic-ui-react';

import {
  get as getProfile,
  post as createProfile,
  patch as editProfile,
  invalidateItem as invalidateProfile,
} from 'actions/teacher-profile';
import { openModal } from 'actions/modal';

import {
  HeaderHead,
  Footer,
  Message,
  ButtonCenterWrapper,
  CenterButton,
  renderFormField,
  getOptions,
  FormWrapper,
} from 'modules/form';

import {
  FIELD_TYPES,
  teacherProfileFields,
  getOnChangeMethod,
  getInputChangeMethod,
} from 'constants/form';

const HeaderHeadWrapper = styled(HeaderHead)`
  padding: 10px;
`;

const getFormDefaults = profileData => {
  const formDefaults = {};

  teacherProfileFields.forEach(field => {
    formDefaults[field.name] =
      !!profileData && profileData[field.param]
        ? profileData[field.param]
        : field.default;
  });
  return formDefaults;
};

class TeacherProfileForm extends PureComponent {
  static defaultProps = {
    teacher: null,
  };

  constructor(props) {
    super(props);
    this.state = {
      isValid: true,
      errorText: null,
      message: null,
      profilePost: store.post(),
      profilePatch: store.patch(),
      profileData: null,
      form: getFormDefaults(),
      formFields: this.getFormFields(),
    };
    teacherProfileFields.forEach(field => {
      const methodName = getOnChangeMethod(field.name);
      this[methodName] = this.onChange.bind(this, field.name);
    });
  }

  static propTypes = {
    actions: PropTypes.shape({
      getProfile: PropTypes.func.isRequired,
      createProfile: PropTypes.func.isRequired,
      editProfile: PropTypes.func.isRequired,
      invalidateProfile: PropTypes.func.isRequired,
    }).isRequired,
    teacher: PropTypes.object,
    userConfig: PropTypes.object.isRequired,
  };

  getFormFields = () => {
    const { userConfig } = this.props;

    return teacherProfileFields.map(field => {
      if (field.fieldType === FIELD_TYPES.SELECT) {
        field.choices = getOptions({ field: field.name, userConfig });
      }
      return field;
    });
  };

  static getDerivedStateFromProps(props, state) {
    const { teacherProfile, teacher, actions } = props;
    const { profilePost, profilePatch, profileData } = state;
    const profileGet = store.get(teacherProfile, teacher.id);
    const newProfilePost = store.post(teacherProfile, profilePost.id);
    const newProfilePatch = store.patch(teacherProfile, profilePatch.id);
    const newState = {};

    // handle get action
    if (profileGet && profileGet.isLoaded && !profileData && !profileGet.isStale) {
      newState.profileData = profileGet.data;
      if (profileGet.data && Object.keys(profileGet.data).length)
        newState.form = getFormDefaults(profileGet.data);
      else {
        actions.invalidateProfile(teacher.id);
      }
    }

    // handle post action
    if (newProfilePost.resolved) {
      if (newProfilePost.resolved.isCreated) {
        newState.profilePost = newProfilePost;
        newState.profileData = newProfilePost.resolved.data;
        newState.message = 'Profile details saved successfully';
      } else if (newProfilePost.resolved.isCreatingFailed) {
        newState.profilePost = newProfilePost;
        newState.errorText = store.getError(newProfilePost.resolved).message;
      }
    }

    // handle patch action
    if (newProfilePatch.resolved) {
      if (newProfilePatch.resolved.isPatched) {
        newState.profilePatch = newProfilePatch;
        newState.profileData = newProfilePatch.resolved.data;
        newState.message = 'Profile details saved successfully';
      } else if (newProfilePatch.resolved.isPatchingFailed) {
        newState.profilePatch = newProfilePatch;
        newState.errorText = store.getError(newProfilePatch.resolved).message;
      }
    }

    return newState;
  }

  componentDidMount() {
    const {
      actions,
      teacher: { id },
      profileData,
    } = this.props;
    if (!profileData) actions.getProfile(id);
    else this.setState(() => ({ form: getFormDefaults(profileData) }));
  }

  onChange = (key, value) => {
    const { form, errorText } = this.state;
    const stateChanges = {
      form: {
        ...form,
        [key]: value,
      },
    };
    if (errorText) stateChanges.errorText = null;
    this.setState(() => ({ ...stateChanges }));
  };

  onToggle = event => {
    const { form, errorText } = this.state;
    const key = event.target.name;
    const value = event.target.checked;
    const stateChanges = {
      form: {
        ...form,
        [key]: value,
      },
    };
    if (errorText) stateChanges.errorText = null;
    this.setState(() => ({ ...stateChanges }));
  };

  onOptionChange = (key, value) => {
    const { form, errorText } = this.state;
    const stateChanges = {
      form: {
        ...form,
        [key]: value,
      },
    };
    if (errorText) stateChanges.errorText = null;
    this.setState(() => ({ ...stateChanges }));
  };

  handlePostAction = () => {
    const { actions, teacher } = this.props;
    const {
      profilePost: { id },
      form,
      formFields,
    } = this.state;
    const data = {};
    formFields.forEach(({ name, param }) => {
      if (param) data[param] = form[name];
    });
    actions.createProfile(id, data, { teacherId: teacher.id });
  };

  handlePatchAction = () => {
    const { actions, teacher } = this.props;
    const {
      profilePatch: { id },
      form,
      profileData,
      formFields,
    } = this.state;
    const data = {};

    formFields.forEach(({ name, param }) => {
      if (param && profileData[param] !== form[name]) data[param] = form[name];
    });
    if (Object.keys(data).length) actions.editProfile(id, teacher.id, data);
  };

  onSubmit = event => {
    const { profileData } = this.props;
    event.preventDefault();
    if (profileData === null) this.handlePostAction();
    else if (profileData) this.handlePatchAction();
  };

  renderFormHeader = () => {
    const { teacher } = this.props;
    if (teacher) {
      return (
        <HeaderHeadWrapper>
          <Header as="h3" floated="left">
            Teacher Profile
          </Header>
        </HeaderHeadWrapper>
      );
    } else {
      return '';
    }
  };
  renderTeacherManagerInfo = () => {
    const { teacher: { psm_info: psmInfo } = {} } = this.props;
    const { full_name: fullName = '', email = '', phone = '' } = psmInfo || {};
    let teacherManager = '';

    if (fullName || email || phone) teacherManager = `${fullName} | ${email} | ${phone}`;

    return renderFormField({
      name: 'psmInfo',
      label: 'Teacher Manager',
      fieldType: FIELD_TYPES.INPUT,
      disabled: true,
      param: 'psm_info',
      profileView: true,
      value: teacherManager,
    });
  };

  renderFormFields = () => {
    const { formFields, form } = this.state;

    return (
      <FlexView>
        <Grid>
          {formFields.map(field => {
            const disabled = field.disabled || !field.editable;
            const required = field.required;
            const placeholder = disabled ? '' : field.label;

            return (
              <Grid.Column mobile={16} tablet={16} computer={8}>
                {renderFormField({
                  ...field,
                  profileView: true,
                  value: form[field.name],
                  onChange: this[getInputChangeMethod(field)],
                  required,
                  disabled,
                  placeholder,
                })}
              </Grid.Column>
            );
          })}
          <Grid.Column mobile={16} tablet={16} computer={6}>
            {this.renderTeacherManagerInfo()}
          </Grid.Column>
        </Grid>
      </FlexView>
    );
  };

  renderFormFooter = () => {
    const { profileData } = this.props;
    let btnText = 'SAVE';
    if (profileData !== null) {
      btnText = 'UPDATE';
    }

    return (
      <Footer>
        <ButtonCenterWrapper>
          <CenterButton label={btnText} />
        </ButtonCenterWrapper>
      </Footer>
    );
  };

  render() {
    const { errorText, message } = this.state;

    return (
      <form onSubmit={this.onSubmit}>
        <FormWrapper>
          {this.renderFormHeader()}
          {message && <Message>{message}</Message>}
          <Separator transparent height="25" />
          {this.renderFormFields()}
          {errorText && <Message error={true}>{errorText}</Message>}
          <Separator transparent height="50" />
          {this.renderFormFooter()}
          <Separator transparent height="50" />
        </FormWrapper>
      </form>
    );
  }
}

export default connect(
  ({ teacherProfile }) => ({ teacherProfile }),
  dispatch => ({
    actions: bindActionCreators(
      {
        getProfile,
        createProfile,
        editProfile,
        invalidateProfile,
        openModal,
      },
      dispatch,
    ),
  }),
)(TeacherProfileForm);
